import { z } from 'zod';
import { Id, LineKind, Money, Quantity, Timestamp } from './common.js';
import { MaintenanceCategory } from '../maintenance.js';

/**
 * Council n°2 (08/10/2026) : récapitulatif de facturation d'un dossier, copié ou exporté en un geste
 * vers le logiciel de facturation du garage (jamais de facture dans l'outil). Réservé à `amounts.view`.
 * `purpose` dit au journal si le récapitulatif est copié ou téléchargé en CSV.
 */
export const BillingSummaryQuery = z.strictObject({ purpose: z.enum(['copy', 'csv']) });
export type BillingSummaryQuery = z.infer<typeof BillingSummaryQuery>;

/** Date locale du garage (`AAAA-MM-JJ`). */
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const BillingLine = z.object({
  kind: LineKind,
  maintenanceCategory: MaintenanceCategory.nullable(),
  label: z.string(),
  reference: z.string().nullable(),
  quantity: Quantity,
  /** `null` : travail réalisé saisi sans prix (exclu des totaux, signalé). */
  unitPriceHt: Money.nullable(),
  vatRate: z.string(),
  totalHt: Money.nullable(),
  totalVat: Money.nullable(),
  totalTtc: Money.nullable(),
});
export type BillingLine = z.infer<typeof BillingLine>;

export const BillingTotals = z.object({ totalHt: Money, totalVat: Money, totalTtc: Money });
export type BillingTotals = z.infer<typeof BillingTotals>;

export const BillingSection = z.object({
  kind: z.enum(['work', 'complement']),
  /** « Travaux réalisés » ou titre des travaux complémentaires. */
  title: z.string(),
  /** Version acceptée exacte (travaux complémentaires seulement). */
  complementVersion: z.number().int().nullable(),
  acceptedAt: Timestamp.nullable(),
  lines: z.array(BillingLine),
  totals: BillingTotals,
});
export type BillingSection = z.infer<typeof BillingSection>;

export const BillingSummaryOutput = z.object({
  interventionId: Id,
  generatedAt: Timestamp,
  vehicle: z.object({
    plate: z.string(),
    make: z.string(),
    model: z.string(),
    entryMileage: z.number().int(),
    exitMileage: z.number().int().nullable(),
  }),
  customer: z.object({ name: z.string(), companyName: z.string().nullable() }),
  receivedOn: LocalDate,
  releasedOn: LocalDate.nullable(),
  sections: z.array(BillingSection),
  /** Lignes sans prix (exclues des totaux) : à compléter avant de facturer. */
  unpricedLines: z.number().int(),
  vatBreakdown: z.array(z.object({ vatRate: z.string(), totalHt: Money, totalVat: Money })),
  totals: BillingTotals,
});
export type BillingSummaryOutput = z.infer<typeof BillingSummaryOutput>;
